
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface FileTypeOption {
  label: string;
  extensions: string[];
  icon: React.ReactNode;
}

interface FileUploadMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectFileType: (fileType: FileTypeOption) => void;
  isMobile?: boolean;
}

export default function FileUploadMenu({ isOpen, onClose, onSelectFileType, isMobile = false }: FileUploadMenuProps) {
  const [highlightedOption, setHighlightedOption] = useState<string | null>(null);

  const fileTypes: FileTypeOption[] = [
    {
      label: 'PDF Document',
      extensions: ['.pdf'],
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path 
            d="M20 8.94C19.9896 8.84813 19.9695 8.75763 19.94 8.67V8.58C19.8919 8.47718 19.8278 8.38267 19.75 8.3L13.75 2.3C13.6673 2.22222 13.5728 2.15808 13.47 2.11C13.4402 2.10576 13.4099 2.10576 13.38 2.11C13.2784 2.05174 13.1662 2.01434 13.05 2H7C6.46957 2 5.96086 2.21071 5.58579 2.58579C5.21071 2.96086 5 3.46957 5 4V20C5 20.5304 5.21071 21.0391 5.58579 21.4142C5.96086 21.7893 6.46957 22 7 22H17C17.5304 22 18.0391 21.7893 18.4142 21.4142C18.7893 21.0391 19 20.5304 19 20V9C19 9 19 9 19 8.94ZM14 5.41L15.59 7H14V5.41ZM17 20H7V4H12V7C12 7.26522 12.1054 7.51957 12.2929 7.70711C12.4804 7.89464 12.7348 8 13 8H17V20Z" 
            fill="#E74C3C"
          />
          <path 
            d="M14.25 13C14.25 13.7 13.9 14.25 13.4 14.55C13.9 14.85 14.35 15.4 14.35 16.15C14.35 17.25 13.45 18 12.35 18H9.5V10H12.15C13.2 10 14.25 10.65 14.25 13ZM11 11.5V13H12.15C12.6 13 12.75 12.5 12.75 12C12.75 11.5 12.45 11.5 12.15 11.5H11ZM11 14.5V16.5H12.35C12.8 16.5 12.85 16 12.85 15.5C12.85 14.85 12.45 14.5 12.1 14.5H11Z" 
            fill="#E74C3C"
          />
        </svg>
      ),
    },
    {
      label: 'Document',
      extensions: ['.doc', '.docx', '.txt', '.rtf'],
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path 
            d="M14 2H6C4.9 2 4.01 2.9 4.01 4L4 20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z" 
            fill="#4B81FF"
          />
          <path 
            d="M8 14H16V16H8V14ZM8 10H16V12H8V10Z" 
            fill="#4B81FF"
          />
        </svg>
      ),
    },
    {
      label: 'Spreadsheet',
      extensions: ['.xls', '.xlsx', '.csv'],
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path 
            d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19Z" 
            fill="#27AE60"
          />
          <path 
            d="M7 13H9V17H7V13ZM11 7H13V17H11V7ZM15 10H17V17H15V10Z" 
            fill="#27AE60"
          />
        </svg>
      ),
    }
  ];

  return (
    <motion.div
      className={`bg-white rounded-xl shadow-[0px_8px_35px_rgba(22,91,251,0.08)] overflow-hidden border border-[#e8ecf5]/80 relative ${isMobile ? 'w-full max-w-[280px]' : 'w-[280px]'}`}
      initial={{ opacity: 0, y: 10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95, y: 10 }}
      transition={{ type: "spring", damping: 25 }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Mobile close button - only shown on mobile */}
      {isMobile && (
        <button 
          className="absolute right-2 top-2 text-gray-400 hover:text-gray-700 z-10"
          onClick={onClose}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
          </svg>
        </button>
      )}
      
      {/* Enhanced subtle glow effect behind the menu */}
      <div className="absolute inset-0 bg-[#4b81ff]/3 blur-xl z-[-1] rounded-xl"></div>
      {/* Extra ambient glow for depth */}
      <div className="absolute inset-2 bg-white/60 blur-lg z-[-1] rounded-xl"></div>
      <div className="p-4 bg-gradient-to-b from-white to-[#fafbff]">
        <h3 className="text-[16px] font-medium text-[#464C5C] mb-1">Upload a file</h3>
        <p className="text-[14px] text-[#717182] mb-4">Select file type to upload</p>
        
        <div className="flex flex-col gap-2">
          {fileTypes.map((fileType) => (
            <motion.div
              key={fileType.label}
              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                highlightedOption === fileType.label ? 'bg-[#F6F8FF] shadow-sm' : 'hover:bg-[#f9faff] hover:shadow-sm'
              }`}
              onClick={() => onSelectFileType(fileType)}
              onMouseEnter={() => setHighlightedOption(fileType.label)}
              onMouseLeave={() => setHighlightedOption(null)}
              whileHover={{ x: 2 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex-shrink-0 bg-[#f6f8ff] p-2 rounded-md shadow-sm">{fileType.icon}</div>
              <div>
                <p className="text-[15px] font-medium text-[#333333]">{fileType.label}</p>
                <p className="text-[13px] text-[#717182]">
                  {fileType.extensions.join(', ')}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
